import { useRef, useState } from "react";
import api from "@/lib/api";

/**
 * VoiceRecorder — push-to-record button that captures up to 120 seconds of audio,
 * encodes to base64 data URL, and POSTs /api/social/voice-message.
 */
export default function VoiceRecorder({ toUserId, onSent, disabled }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [err, setErr] = useState("");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const startRef = useRef(0);
  const tickRef = useRef(null);

  async function start() {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = e => { if (e.data?.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const reader = new FileReader();
        reader.onload = async () => {
          try {
            const duration = Math.min(120, (Date.now() - startRef.current) / 1000);
            const { data } = await api.post("/social/voice-message", {
              to: toUserId, audio_data_url: reader.result, duration_sec: duration,
            });
            onSent && onSent(data);
          } catch (e) { setErr(e.response?.data?.detail || "Send failed"); }
        };
        reader.readAsDataURL(blob);
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRef.current = mr;
      mr.start();
      startRef.current = Date.now();
      setRecording(true);
      setElapsed(0);
      tickRef.current = setInterval(() => {
        const s = (Date.now() - startRef.current) / 1000;
        setElapsed(s);
        if (s >= 120) stop();
      }, 200);
    } catch (e) {
      setErr("Microphone access denied.");
    }
  }

  function stop() {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    setRecording(false);
    try { mediaRef.current?.stop(); } catch {}
  }

  return (
    <div className="inline-flex items-center gap-2">
      {recording ? (
        <button data-testid="voice-stop" type="button" onClick={stop}
          className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded text-xs font-bold flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-white animate-pulse"/> {elapsed.toFixed(1)}s · Stop
        </button>
      ) : (
        <button data-testid="voice-record" type="button" disabled={disabled} onClick={start} title="Record voice message"
          className={`px-2.5 py-1.5 rounded text-xs ${disabled ? "bg-slate-800 text-slate-600" : "bg-slate-700 hover:bg-slate-600 text-amber-300"}`}>
          <i className="fa-solid fa-microphone"/>
        </button>
      )}
      {err && <span className="text-[10px] text-rose-300">{err}</span>}
    </div>
  );
}
